import React, { useState, useEffect, useMemo } from 'react';
import { useOrderContext } from '../context/OrderContext';
import { usePriceContext } from '../context/PriceContext';
import { useStatusContext } from '../context/StatusContext';
import { useUserContext } from '../context/UserContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faXmark, faMagnifyingGlass, faTrash,
} from '@fortawesome/free-solid-svg-icons';

const OrderModal = ({ order, onClose }) => {
  const { addOrder, updateOrder, fetchNextNumber } = useOrderContext();
  const { status } = useStatusContext();
  const { user } = useUserContext();
  const { price, fetchPrice } = usePriceContext();

  const isEdit = !!order;
  const [number, setNumber] = useState(order?.number || '');
  const [kind, setKind] = useState(order?.kind || 'supplier');
  const [forWhom, setForWhom] = useState(order?.forWhom || '');
  const [deadline, setDeadline] = useState(
    order?.deadline ? new Date(order.deadline).toISOString().slice(0, 10) : ''
  );
  const [stat, setStat] = useState(order?.status || '');
  const [comment, setComment] = useState(order?.comment || '');
  const [items, setItems] = useState(order?.items || []);

  // Для создания — тянем номер и статус «Запланировано»
  useEffect(() => {
    if (!isEdit) {
      fetchNextNumber().then(n => setNumber(n));
    }
  }, [isEdit, fetchNextNumber]);

  useEffect(() => {
    if (!price.length) fetchPrice();
  }, [price.length, fetchPrice]);

  useEffect(() => {
    if (!stat && status?.length) {
      const open = status.find(s => s.name?.toLowerCase() === 'запланировано');
      setStat(open?._id || status[0]._id);
    }
  }, [status, stat]);

  // ---- Выбор номенклатуры ----
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return price.slice(0, 100);
    return price.filter(p =>
      (p.partNumber || '').toLowerCase().includes(q) ||
      (p.name || '').toLowerCase().includes(q) ||
      (p.brand || '').toLowerCase().includes(q)
    ).slice(0, 100);
  }, [price, query]);

  const addItem = (p) => {
    setItems(prev => {
      const idx = prev.findIndex(i => i.priceId === p._id);
      if (idx !== -1) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], qty: copy[idx].qty + 1 };
        return copy;
      }
      return [...prev, {
        priceId: p._id,
        partNumber: p.partNumber || '',
        name: p.name || '',
        qty: 1,
      }];
    });
  };

  const changeQty = (priceId, qty) => {
    const n = Math.max(1, parseInt(qty, 10) || 1);
    setItems(prev => prev.map(i => i.priceId === priceId ? { ...i, qty: n } : i));
  };

  const removeItem = (priceId) => setItems(prev => prev.filter(i => i.priceId !== priceId));

  const statusList = useMemo(() => {
    if (!status) return [];
    if (isEdit) {
      // при редактировании можно поставить «Запланировано», «В работе», «Завершено»
      const allowed = ['запланировано', 'в работе', 'завершено'];
      return status.filter(s => allowed.includes((s.name || '').toLowerCase()));
    }
    // при создании — только «Запланировано»
    return status.filter(s => (s.name || '').toLowerCase() === 'запланировано');
  }, [status, isEdit]);

  const handleSave = async () => {
    if (!number.trim() || !forWhom.trim() || items.length === 0) {
      alert('Заполните номер, «для кого» и добавьте хотя бы одну позицию');
      return;
    }
    const payload = {
      number: number.trim(),
      kind,
      forWhom: forWhom.trim(),
      deadline: deadline ? new Date(deadline).getTime() : null,
      status: stat,
      comment,
      items,
      creatorName: order?.creatorName || user || '',
    };
    try {
      if (isEdit) await updateOrder(order._id, payload);
      else await addOrder(payload);
      onClose();
    } catch (e) { alert(e.message); }
  };

  return (
    <div className="modal-overlay">
      <div className="task-modal admin-modal order-modal">
        <header className="task-modal__header">
          <h2 className="task-modal__title">
            {isEdit ? `Заказ № ${order.number}` : 'Новый заказ'}
          </h2>
          <button className="task-modal__close" onClick={onClose}>×</button>
        </header>

        <div className="task-modal__body">
          <div className="admin-modal__grid">
            <div className="admin-modal__field">
              <label className="admin-modal__label">Номер заказа</label>
              <input
                className="admin-modal__input"
                value={number}
                onChange={e => setNumber(e.target.value)}
                disabled={isEdit}
                title={isEdit ? 'Номер нельзя менять' : ''}
              />
            </div>
            <div className="admin-modal__field">
              <label className="admin-modal__label">Тип заказа</label>
              <select
                className="admin-modal__select"
                value={kind}
                onChange={e => setKind(e.target.value)}
                disabled={isEdit}
              >
                <option value="supplier">Заказ поставщику</option>
                <option value="client">Заказ под клиента</option>
              </select>
            </div>
            <div className="admin-modal__field">
              <label className="admin-modal__label">Для кого</label>
              <input
                className="admin-modal__input"
                value={forWhom}
                onChange={e => setForWhom(e.target.value)}
                placeholder="Контрагент / клиент"
              />
            </div>
            <div className="admin-modal__field">
              <label className="admin-modal__label">Срок исполнения</label>
              <input
                type="date"
                className="admin-modal__input"
                value={deadline}
                onChange={e => setDeadline(e.target.value)}
              />
            </div>
            <div className="admin-modal__field">
              <label className="admin-modal__label">Статус</label>
              <select
                className="admin-modal__select"
                value={stat}
                onChange={e => setStat(e.target.value)}
              >
                {statusList.map(s => (
                  <option key={s._id} value={s._id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div className="admin-modal__field admin-modal__field--full">
              <label className="admin-modal__label">Комментарий</label>
              <textarea
                className="admin-modal__textarea"
                value={comment}
                onChange={e => setComment(e.target.value)}
              />
            </div>
          </div>

          {/* Позиции заказа */}
          <div className="order-items">
            <div className="order-items__head">
              <span>Позиции заказа ({items.length})</span>
              <button className="order-items__add" onClick={() => setPickerOpen(true)}>
                <FontAwesomeIcon icon={faPlus} /> Добавить из прайса
              </button>
            </div>

            {items.length === 0 && (
              <div className="order-items__empty">Позиций пока нет</div>
            )}

            {items.map(it => (
              <div key={it.priceId} className="order-items__row">
                <span className="order-items__article">{it.partNumber}</span>
                <span className="order-items__name" title={it.name}>
                  {it.name.length > 40 ? it.name.slice(0, 40) + '…' : it.name}
                </span>
                <input
                  type="number"
                  min={1}
                  className="order-items__qty"
                  value={it.qty}
                  onChange={e => changeQty(it.priceId, e.target.value)}
                />
                <button className="order-items__remove" onClick={() => removeItem(it.priceId)} title="Убрать">
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
            ))}
          </div>

          {/* Пicker номенклатуры */}
          {pickerOpen && (
            <div className="order-picker">
              <div className="order-picker__head">
                <div className="order-picker__search">
                  <FontAwesomeIcon icon={faMagnifyingGlass} />
                  <input
                    autoFocus
                    placeholder="Поиск по артикулу, названию, бренду"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                  />
                </div>
                <button className="order-picker__close" onClick={() => setPickerOpen(false)}>
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
              <div className="order-picker__list">
                {filtered.length === 0 && <div className="order-picker__empty">Ничего не найдено</div>}
                {filtered.map(p => {
                  const added = items.find(i => i.priceId === p._id);
                  return (
                    <div
                      key={p._id}
                      className={`order-picker__item ${added ? 'order-picker__item--added' : ''}`}
                      onClick={() => addItem(p)}
                    >
                      <span className="order-picker__article">{p.partNumber}</span>
                      <span className="order-picker__name" title={p.name}>
                        {(p.name || '').length > 40 ? p.name.slice(0, 40) + '…' : p.name}
                      </span>
                      {added && <span className="order-picker__badge">{added.qty}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <footer className="task-modal__footer">
          <button className="task-modal__btn task-modal__btn--ghost" onClick={onClose}>Отмена</button>
          <button className="task-modal__btn task-modal__btn--primary" onClick={handleSave}>Сохранить</button>
        </footer>
      </div>
    </div>
  );
};

export default OrderModal;